import * as _ from 'underscore';

import { AlertListMapper } from '../mappers/AlertMapper';
import { ContentMapper } from '../mappers/ContentMapper';
import { DataAlertMapper } from '../mappers/DataAlertMapper';
import { DataConnectionMapper } from '../mappers/DataConnectionMapper';
import { DataRoleMapper } from '../mappers/DataRoleMapper';
import { DatasourceMapper } from '../mappers/DatasourceMapper';
import { ExtractTaskMapper } from '../mappers/ExtractTaskMapper';
import { FlowMapper } from '../mappers/FlowMapper';
import { FlowOutputStepMapper } from '../mappers/FlowOutputStepMapper';
import { FlowOutputStepRunMapper } from '../mappers/FlowOutputStepRunMapper';
import { MetricMapper } from '../mappers/MetricMapper';
import { ProjectMapper } from '../mappers/ProjectMapper';
import { RemoteRefreshAgentMapper } from '../mappers/RemoteRefreshAgentMapper';
import { RemoteRefreshScheduleMapper } from '../mappers/RemoteRefreshScheduleMapper';
import { RunFlowTaskMapper } from '../mappers/RunFlowTaskMapper';
import { SubscriptionMapper } from '../mappers/SubscriptionMapper';
import { UserMapper } from '../mappers/UserMapper';
import { ViewMapper } from '../mappers/ViewMapper';
import { WorkbookMapper } from '../mappers/WorkbookMapper';
import { FlowListItemInfo } from '../models/FlowInfo';
import { FlowOutputStepInfo } from '../models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo } from '../models/FlowOutputStepRunInfo';
import { IAlertListInfo } from '../models/IAlertInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { IDataConnectionInfo } from '../models/IDataConnectionInfo';
import { IDataRoleInfo } from '../models/IDataRoleInfo';
import { IDataSourceInfo } from '../models/IDataSourceInfo';
import { IExtractTaskInfo } from '../models/IExtractTaskInfo';
import { IGroupInfo } from '../models/IGroupInfo';
import { IMetricInfo } from '../models/IMetricInfo';
import { IProjectInfo } from '../models/IProjectInfo';
import { IRecentContentInfo } from '../models/IRecentContentInfo';
import { IRemoteRefreshAgentInfo } from '../models/IRemoteRefreshAgentInfo';
import { IRemoteRefreshScheduleInfo } from '../models/IRemoteRefreshScheduleInfo';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { ISubscriptionInfo } from '../models/ISubscriptionInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IViewRecommendationInfo } from '../models/IViewRecommendationInfo';
import { IWorkbookInfo } from '../models/IWorkbookInfo';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { IExploreContentResult } from '../ServerApi/requests/ExploreContentRequest';
import { IGetDataAlertsResult } from '../ServerApi/requests/GetDataAlertsRequest';
import { IGetDataConnectionsResult } from '../ServerApi/requests/GetDataConnectionsRequest';
import { IGetDataRolesResult } from '../ServerApi/requests/GetDataRolesRequest';
import { IGetDatasourcesResult } from '../ServerApi/requests/GetDatasourcesRequest';
import { IGetDetailedRecentsResult } from '../ServerApi/requests/GetDetailedRecentsRequest';
import { IGetExtractTasksResult } from '../ServerApi/requests/GetExtractTasksRequest';
import { IGetFieldValuesResult } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetFlowOutputStepRunsResult } from '../ServerApi/requests/GetFlowOutputStepRunsRequest';
import { GetFlowOutputStepsResult } from '../ServerApi/requests/GetFlowOutputStepsRequest';
import { GetFlowsResult } from '../ServerApi/requests/GetFlowsRequest';
import { IGetGroupsResult } from '../ServerApi/requests/GetGroupsRequest';
import { IGetMetricsResult } from '../ServerApi/requests/GetMetricsRequest';
import { IGetProjectsResult } from '../ServerApi/requests/GetProjectsRequest';
import { IGetRecommendedViewsResult } from '../ServerApi/requests/GetRecommendedViewsRequest';
import { IGetRemoteRefreshAgentsResult } from '../ServerApi/requests/GetRemoteRefreshAgentsRequest';
import { IRemoteRefreshSchedulesResult } from '../ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { GetRunFlowTasksResult } from '../ServerApi/requests/GetRunFlowTasksRequest';
import { IGetServerUsersResult } from '../ServerApi/requests/GetServerUsersRequest';
import { IGetSiteUsersResult } from '../ServerApi/requests/GetSiteUsersRequest';
import { IGetSubscriptionsResult } from '../ServerApi/requests/GetSubscriptionsRequest';
import { IGetViewsResult } from '../ServerApi/requests/GetViewsRequest';
import { IGetWorkbooksResult } from '../ServerApi/requests/GetWorkbooksRequest';
import { IAlertList } from '../ServerApi/types/IAlert';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IGroup } from '../ServerApi/types/IGroup';
import { IRecent } from '../ServerApi/types/IRecent';
import { IServerUser } from '../ServerApi/types/IServerUser';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { IMetricUsageInfo } from '../ServerApi/types/IUsageInfo';
import { IViewRecommendation } from '../ServerApi/types/IViewRecommendation';
import { IIdItem, IPathItem } from '../ServerApi/types/resourceParts';

export module ServerApiMapper {

  export function content(exploreContentResult: IExploreContentResult): IContentInfo[] {
    if(!exploreContentResult.content || exploreContentResult.content.length <= 0) return [];

    const users =  index(UserMapper.map(exploreContentResult.users));

    return ContentMapper.map(exploreContentResult.content, users);
  }

  export function projects(projectResult: IGetProjectsResult): IProjectInfo[] {
    if(!projectResult.projects || projectResult.projects.length <= 0) return [];

    const users = index(UserMapper.map(projectResult.users));

    return ProjectMapper.map(projectResult.projects, users, favorites(projectResult.favorites));
  }

  export function dataRoles(dataRoleResult: IGetDataRolesResult): IDataRoleInfo[] {
    if(!dataRoleResult.dataRoles || dataRoleResult.dataRoles.length <= 0) return [];

    const users = index(UserMapper.map(dataRoleResult.users));
    const projects = index(ProjectMapper.mapNames(dataRoleResult.projects));

    return DataRoleMapper.map(dataRoleResult.dataRoles, users, favorites(dataRoleResult.favorites), projects);
  }

  export function dataConnections(dataConnectionsResult: IGetDataConnectionsResult): IDataConnectionInfo[] {
    if (!dataConnectionsResult.dataConnections || dataConnectionsResult.dataConnections.length <= 0) {
      return [];
    }

    const users = index(UserMapper.map(dataConnectionsResult.users));
    const projects = index(ProjectMapper.mapNames(dataConnectionsResult.projects));
    const workbooks = index(dataConnectionsResult.workbooks);
    const dataSources = dataConnectionsResult.datasources
      ? index(DatasourceMapper.mapBasic(dataConnectionsResult.datasources, users, projects, workbooks))
      : {};
    const flows = dataConnectionsResult.flows
      ? index(FlowMapper.mapBasic(dataConnectionsResult.flows, users, projects))
      : {};

    return DataConnectionMapper.map(dataConnectionsResult.dataConnections, dataSources, flows);
  }

  export function dataSources(dataSourcesResult: IGetDatasourcesResult): IDataSourceInfo[] {
    if(!dataSourcesResult.datasources || dataSourcesResult.datasources.length <= 0) return [];

    const users = index(UserMapper.map(dataSourcesResult.users));
    const projects = index(ProjectMapper.mapNames(dataSourcesResult.projects));
    const workbooks = index(dataSourcesResult.workbooks);

    return DatasourceMapper.map(dataSourcesResult.datasources, users, projects, workbooks, dataSourcesResult.maxStats,
      favorites(dataSourcesResult.favorites));
  }

  export function workbooks(workbooksResult: IGetWorkbooksResult): IWorkbookInfo[] {
    if(!workbooksResult.workbooks || workbooksResult.workbooks.length <= 0) return [];

    const users = index(UserMapper.map(workbooksResult.users));
    const projects = index(ProjectMapper.mapNames(workbooksResult.projects));

    return WorkbookMapper.map(workbooksResult.workbooks, users, projects, workbooksResult.maxStats,
      favorites(workbooksResult.favorites));
  }

  export function views(viewResult: IGetViewsResult): IViewInfo[] {
    if(!viewResult.views || viewResult.views.length <= 0) return [];

    const users = index(UserMapper.map(viewResult.users));
    const projects = index(ProjectMapper.mapNames(viewResult.projects));
    const workbooks = index(WorkbookMapper.mapParents(viewResult.workbooks, users, projects));
    const recents = _.indexBy(viewResult.recents, 'objectId');

    return ViewMapper.map(viewResult.views, workbooks, viewResult.maxStats, favorites(viewResult.favorites), recents);
  }

  export function recentItems(recentsResult: IGetDetailedRecentsResult): IRecentContentInfo[] {
    if (!recentsResult.recents || recentsResult.recents.length <= 0){
      return [];
    }

    const users = index(UserMapper.map(recentsResult.users));
    const projects = index(ProjectMapper.mapNames(recentsResult.projects));
    const workbooks = index(WorkbookMapper.mapParents(recentsResult.workbooks, users, projects));
    const views = index(ViewMapper.map(recentsResult.views, workbooks, recentsResult.viewMaxStats, favorites(recentsResult.viewFavorites)));
    const connectedViews = recentsResult.connectedViews ? indexByPath(MetricMapper.mapConnectedViews(recentsResult.connectedViews)) : {};
    const metrics = recentsResult.metrics ? index(MetricMapper.map(recentsResult.metrics, users, projects, connectedViews, <IMetricUsageInfo>{}, favorites(recentsResult.metricFavorites)))
      : {};

    const itemsInRecentOrder: IRecentContentInfo[] = recentsResult.recents
      .filter((recent: IRecent) => recent.objectType === 'view' || recent.objectType === 'metric')
      .map((recent: IRecent) => {
        if (recent.objectType === 'view') {
          return {...views[recent.objectId], lastAccessedAt: recent.lastAccessedAt};
        } else if (recent.objectType === 'metric') {
          return {...metrics[recent.objectId], lastAccessedAt: recent.lastAccessedAt};
        }
      });

    return itemsInRecentOrder;
  }

  export function metrics(metricsResult: IGetMetricsResult): IMetricInfo[] {
    if(!metricsResult.metrics || metricsResult.metrics.length <= 0) return [];

    const users = index(UserMapper.map(metricsResult.users));
    const connectedViews = indexByPath(MetricMapper.mapConnectedViews(metricsResult.connectedViews));
    const projects = index(ProjectMapper.mapNames(metricsResult.projects));

    return MetricMapper.map(metricsResult.metrics, users, projects, connectedViews, metricsResult.maxStats, favorites(metricsResult.favorites));
  }

  export function vizRecommendations(vizRecommendationsResult: IGetRecommendedViewsResult): IViewRecommendationInfo[] {
    if(!vizRecommendationsResult.recommendations || vizRecommendationsResult.recommendations.length <= 0) return [];

    const users = index(UserMapper.map(vizRecommendationsResult.users));
    const projects = index(ProjectMapper.mapNames(vizRecommendationsResult.projects));
    const workbooks = index(WorkbookMapper.mapParents(vizRecommendationsResult.workbooks, users, projects));
    const views = index(ViewMapper.map(vizRecommendationsResult.views, workbooks, vizRecommendationsResult.viewMaxStats));
    const modelId = vizRecommendationsResult.modelId;

    const vizRecommendationsParsed: IViewRecommendationInfo[] = vizRecommendationsResult.recommendations
      // filter out views from recommendation model that is no longer available in views array
      .filter((vizRec: IViewRecommendation) => views[vizRec.objectId])
      .map((vizRec: IViewRecommendation) => {
        const recommendedView = {...views[vizRec.objectId], contentType: <'recommendedView'> 'recommendedView'};
        return {
            ...recommendedView,
            modelId: modelId,
            hide: false,
            favoritesTotal: recommendedView.usageInfo.favoritesTotal,
            explanationType: vizRec.explanation.type,
            similiarUsers: vizRec.explanation.userIds && vizRec.explanation.userIds.map(userId => users[userId])
          };
      }) || [];
    return vizRecommendationsParsed;
  }

  export function flows(flowsResult: GetFlowsResult): FlowListItemInfo[] {
    if(!flowsResult.flows || flowsResult.flows.length <= 0) return [];

    const users = index(UserMapper.map(flowsResult.users));
    const projects = index(ProjectMapper.mapNames(flowsResult.projects));

    return FlowMapper.map(flowsResult.flows, users, projects, flowsResult.maxStats, favorites(flowsResult.favorites));
  }


  export function flowOutputSteps(flowOutputStepsResult: GetFlowOutputStepsResult): FlowOutputStepInfo[] {
    if(!flowOutputStepsResult.outputSteps || flowOutputStepsResult.outputSteps.length <= 0) return [];

    const connections = index(flowOutputStepsResult.dataConnections);
    const datasources = index(flowOutputStepsResult.datasources);
    const projects = index(flowOutputStepsResult.projects);

    return FlowOutputStepMapper.map(flowOutputStepsResult.outputSteps, connections, datasources, projects);
  }

  export function flowOutputStepRuns(flowOutputStepRunsResult: GetFlowOutputStepRunsResult) : FlowOutputStepRunListItemInfo[] {
    if (!flowOutputStepRunsResult.flowOutputStepRuns || flowOutputStepRunsResult.flowOutputStepRuns.length <= 0) return [];

    const outputSteps = index(flowOutputStepRunsResult.flowOutputSteps);
    const runs = index(flowOutputStepRunsResult.flowRuns);

    return FlowOutputStepRunMapper.map(flowOutputStepRunsResult.flowOutputStepRuns, outputSteps, runs);
  }

  export function alertList(alertList: IAlertList): IAlertListInfo {
    if(!(alertList && alertList.alerts && alertList.alerts.length > 0)) return { total: 0, alerts: [] };

    const workbooks = index(alertList.workbooks);
    const datasources = index(alertList.datasources);
    const flowOutputStepRuns = index(alertList.flowOutputStepRuns);
    const flowOutputSteps = index(alertList.flowOutputSteps);
    const flows = index(alertList.flows);

    return AlertListMapper.map(alertList.totalCount, alertList.alerts, workbooks, datasources, flowOutputStepRuns, flowOutputSteps, flows);
  }

  export function extractTasks(extractTaskResult: IGetExtractTasksResult): IExtractTaskInfo[] {
    if(_.size(extractTaskResult.tasks) <= 0) return [];

    const schedules =   index(extractTaskResult.schedules);
    const sites =       index(extractTaskResult.sites);
    const workbooks =   index(extractTaskResult.workbooks);
    const datasources = index(extractTaskResult.datasources);

    return ExtractTaskMapper.map(extractTaskResult.tasks, schedules, sites, workbooks, datasources);
  }

  export function runFlowTasks(runFlowTasksResult: GetRunFlowTasksResult): RunFlowTaskInfo[] {
    if (runFlowTasksResult == null) return;
    if (_.size(runFlowTasksResult.tasks) <= 0) return [];

    const schedules =           index(runFlowTasksResult.schedules);
    const sites =               index(runFlowTasksResult.sites);
    const flows =               index(runFlowTasksResult.flows);
    const flowOutputSteps =     index(runFlowTasksResult.flowOutputSteps);
    const flowOutputStepRuns =  runFlowTasksResult.flowOutputStepRuns;
    const flowRuns =            runFlowTasksResult.flowRuns;
    const dataConnections =     index(runFlowTasksResult.dataConnections);

    return RunFlowTaskMapper.map(runFlowTasksResult.tasks, schedules, sites, flows, dataConnections, flowOutputSteps, flowRuns, flowOutputStepRuns);
  }

  export function remoteRefreshSchedules(remoteRefreshSchedulesResult: IRemoteRefreshSchedulesResult): IRemoteRefreshScheduleInfo[] {
    if(_.size(remoteRefreshSchedulesResult.schedules) <= 0) return [];

    return RemoteRefreshScheduleMapper.mapToResource(remoteRefreshSchedulesResult);
  }

  export function remoteRefreshAgents(remoteRefreshAgentsResult: IGetRemoteRefreshAgentsResult): IRemoteRefreshAgentInfo[] {
    if(_.size(remoteRefreshAgentsResult.agents) <= 0) return [];

    return RemoteRefreshAgentMapper.mapToResource(remoteRefreshAgentsResult.agents);
  }

  export function subscriptions(subscriptionResult: IGetSubscriptionsResult): ISubscriptionInfo[] {
    if(_.size(subscriptionResult.subscriptions) <= 0) return [];

    return SubscriptionMapper.map(
      subscriptionResult.subscriptions,
      index(subscriptionResult.schedules),
      index(subscriptionResult.sites),
      index(subscriptionResult.users)
    );
  }

  export function dataAlerts(dataAlertsResult: IGetDataAlertsResult): IDataAlertInfo[] {
    if(_.size(dataAlertsResult.dataAlerts) <= 0) return [];

    return DataAlertMapper.map(
      dataAlertsResult.dataAlerts,
      index(dataAlertsResult.users),
      index(dataAlertsResult.groups)
    );
  }

  export function groups(groupsResult: IGetGroupsResult): IGroupInfo[] {
    return groupsResult.groups.map((group: IGroup) => {
      const groupInfo: IGroupInfo = {
        ...group,
        contentType: 'group'
      };
      return groupInfo;
    });
  }

  export function siteUsers(siteUsersResult: IGetSiteUsersResult): ISiteUserInfo[] {
    return siteUsersResult.users.map((user: ISiteUser) => {
      const userInfo: ISiteUserInfo = {
        ...user,
        contentType: 'siteuser'
      };
      return userInfo;
    });
  }

  export function serverUsers(serverUsersResult: IGetServerUsersResult): IServerUserInfo[] {
    return serverUsersResult.users.map((user: IServerUser) => {
      const userInfo: IServerUserInfo = {
        ...user,
        contentType: 'serveruser'
      };
      return userInfo;
    });
  }

  export function fieldValues(fieldValueResult: IGetFieldValuesResult): IFieldValue[] {
    return fieldValueResult.fieldValues;
  }

  function favorites(ids: string[]) {
    return _.indexBy(ids, _.identity);
  }

  function index<T extends IIdItem>(items: ReadonlyArray<T>): _.Dictionary<T> {
    return _.indexBy(items, 'id');
  }

  function indexByPath<T extends IPathItem>(items: ReadonlyArray<T>): _.Dictionary<T> {
    return _.indexBy(items, 'path');
  }
}
